import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { media } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { StatusBadge } from "./ui";
import { Lightbox } from "./Lightbox";

export const Gallery = ({ photos = [], title, status }) => {
  const [ref, embla] = useEmblaCarousel({ direction: "rtl", loop: photos.length > 1 });
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!embla) return undefined;
    const on = () => setIdx(embla.selectedScrollSnap());
    embla.on("select", on);
    return () => { embla.off("select", on); };
  }, [embla]);

  const prev = useCallback(() => embla?.scrollPrev(), [embla]);
  const next = useCallback(() => embla?.scrollNext(), [embla]);

  return (
    <div className="relative" data-testid="vehicle-gallery">
      <div className="relative">
      <div ref={ref} className="overflow-hidden bg-black" dir="rtl">
        <div className="flex">
          {photos.map((p, i) => (
            <div key={p.id} className="min-w-0 flex-[0_0_100%]">
              <button type="button" className="block w-full cursor-zoom-in" onClick={() => setOpen(true)} aria-label={`${t("a11y.open_photo")} ${i + 1}`} data-testid={`gallery-open-${i}`}>
                <img src={media(p.id)} alt={`${title} ${i + 1}`} loading={i ? "lazy" : "eager"} draggable={false} className="w-full aspect-[4/3] md:aspect-[16/9] object-cover select-none" />
              </button>
            </div>
          ))}
        </div>
      </div>
      <StatusBadge status={status} className="absolute top-4 start-4 !text-sm !px-4 !py-1.5" />
      {photos.length > 1 && (
        <>
          <button data-testid="gallery-prev" aria-label={t("a11y.prev")} onClick={prev} className="absolute top-1/2 start-3 -translate-y-1/2 h-11 w-11 grid place-items-center bg-black/60 backdrop-blur border border-white/10 hover:border-white/40">
            <ChevronRight size={20} />
          </button>
          <button data-testid="gallery-next" aria-label={t("a11y.next")} onClick={next} className="absolute top-1/2 end-3 -translate-y-1/2 h-11 w-11 grid place-items-center bg-black/60 backdrop-blur border border-white/10 hover:border-white/40">
            <ChevronLeft size={20} />
          </button>
          <span className="absolute bottom-3 end-3 bg-black/70 px-3 py-1 text-xs ltr-num" data-testid="gallery-counter">{idx + 1} / {photos.length}</span>
        </>
      )}
      </div>
      {photos.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar px-4 md:px-0">
          {photos.map((p, i) => (
            <button key={p.id} data-testid={`gallery-thumb-${i}`} aria-label={`${t("a11y.photo")} ${i + 1}`} aria-current={i === idx} onClick={() => embla?.scrollTo(i)}
              className={`shrink-0 w-20 h-14 overflow-hidden border transition-opacity ${i === idx ? "border-[var(--xd-red)] opacity-100" : "border-transparent opacity-50 hover:opacity-90"}`}>
              <img src={media(p.id, "thumb")} alt="" className="h-full w-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
      {open && photos.length > 0 && <Lightbox photos={photos} start={idx} title={title} onClose={close} />}
    </div>
  );
};
